import { useMemo } from 'react';

import { Column } from '@tanstack/react-table';

import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/new/components/shadcn/multi-select';
import { PopoverContent } from '@/new/components/shadcn/popover';
import { File } from '@/types';

function FilterMultiSelect({
  values,
  options,
  column,
  placeholder,
}: {
  values: string[];
  options: string[];
  column: Column<File, unknown>;
  placeholder: string;
}) {
  return (
    <MultiSelect
      values={values}
      onValuesChange={(values) => {
        column.setFilterValue(values.length > 0 ? values : undefined);
      }}
    >
      <MultiSelectTrigger className="w-full">
        <MultiSelectValue placeholder={placeholder} />
      </MultiSelectTrigger>
      <MultiSelectContent>
        <MultiSelectGroup>
          {options
            .filter((type) => type) // remove falsey values
            .map((type) => (
              <MultiSelectItem key={type} value={type}>
                {type}
              </MultiSelectItem>
            ))}
        </MultiSelectGroup>
      </MultiSelectContent>
    </MultiSelect>
  );
}

export function FileFilterPopover({
  files,
  extensionColumn,
  onClearFilters,
}: {
  files: File[];
  extensionColumn?: Column<File, unknown>;
  onClearFilters: () => void;
}) {
  const fileExtensions = useMemo(() => {
    const extensions = files
      .map((file) => file.name.split('.').pop()?.toUpperCase())
      .filter((ext): ext is string => !!ext);
    return Array.from(new Set(extensions));
  }, [files]);

  const extensionFilterValue = extensionColumn?.getFilterValue();
  const selectedExtensions = Array.isArray(extensionFilterValue) ? extensionFilterValue : [];

  return (
    <PopoverContent side="bottom" className="w-96" sideOffset={8}>
      <div className="-mx-4 -mt-4 rounded-t-md border-b bg-gray-50 p-4 font-medium text-gray-700">Filters</div>

      <div className="mt-4">
        {extensionColumn && (
          <>
            <p className="my-3 text-gray-600">File Extension</p>
            <FilterMultiSelect
              values={selectedExtensions}
              options={fileExtensions}
              column={extensionColumn}
              placeholder="Select file extensions..."
            />
          </>
        )}
      </div>

      <div className="mt-4 flex w-full justify-end">
        <Button onClick={onClearFilters}>
          <Icon className="lucide--funnel-x" />
          <span>Clear All Filters</span>
        </Button>
      </div>
    </PopoverContent>
  );
}
